import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import { router } from '@/router';
import { AuthProvider } from '@/components/providers/auth-provider.component';
import { I18nProvider } from '@/components/providers/i18n-provider.component';
import { QueryProvider } from '@/components/providers/query-provider.component';
import { PermissionsProvider } from '@/context/permissions.context';
import { TablePreferencesProvider } from '@/components/providers/table-preferences-provider.component';
import { DashboardPreferencesProvider } from '@/components/providers/dashboard-preferences-provider.component';
import { SessionExpiryWarning } from '@/components/shared/session-expiry-warning';
import { OfflineBanner } from '@/components/shared/offline-banner';
import { initializeTheme } from '@/styles/theme.util';

function migrateLocalStorage() {
  try {
    const keys = Object.keys(localStorage);

    // Migrate dt-vis-* → dt-column-visibility (consolidated object)
    const oldVisKeys = keys.filter((k) => k.startsWith('dt-vis-'));
    if (oldVisKeys.length > 0) {
      const consolidated: Record<string, unknown> = JSON.parse(
        localStorage.getItem('dt-column-visibility') ?? '{}'
      );
      for (const key of oldVisKeys) {
        const tableKey = key.slice('dt-vis-'.length);
        try { consolidated[tableKey] = JSON.parse(localStorage.getItem(key) ?? '{}'); } catch { /* skip */ }
        localStorage.removeItem(key);
      }
      localStorage.setItem('dt-column-visibility', JSON.stringify(consolidated));
    }

    // Migrate dt-toolbar-* → dt-toolbar-state (consolidated object)
    const oldToolbarKeys = keys.filter((k) => k.startsWith('dt-toolbar-'));
    if (oldToolbarKeys.length > 0) {
      const consolidated: Record<string, boolean> = JSON.parse(
        localStorage.getItem('dt-toolbar-state') ?? '{}'
      );
      for (const key of oldToolbarKeys) {
        const tableKey = key.slice('dt-toolbar-'.length);
        consolidated[tableKey] = localStorage.getItem(key) !== 'false';
        localStorage.removeItem(key);
      }
      localStorage.setItem('dt-toolbar-state', JSON.stringify(consolidated));
    }

    // Remove orphan keys from deleted context files
    localStorage.removeItem('agrovet_table_preferences');
    localStorage.removeItem('agrovet_dashboard_preferences');
  } catch { /* ignore — storage may be unavailable */ }
}

function AppContent() {
  const { t } = useTranslation();

  useEffect(() => {
    migrateLocalStorage();
    initializeTheme();
    document.title = t('common.appName');
  }, [t]);

  return (
    <QueryProvider>
      <AuthProvider>
        <PermissionsProvider>
          <TablePreferencesProvider>
            <DashboardPreferencesProvider>
              <RouterProvider router={router} />
            </DashboardPreferencesProvider>
          </TablePreferencesProvider>
        </PermissionsProvider>
        <SessionExpiryWarning />
      </AuthProvider>
      <OfflineBanner />
      <Toaster position="top-right" richColors />
    </QueryProvider>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AppContent />
    </I18nProvider>
  );
}
